/*!
 * soundFiles.js — the sound of capture() as a file: WAV or FLAC, any channels
 * © 2026 Rob Canning — GPLv3
 *
 * No Web Audio here (the node tests load it). capture.js sends a file a
 * piece at a time as it records, and patches its sizes at the end:
 *
 *   WAV   16 or 24 bit, as many channels as the speakers. More than two (or
 *         24 bit) is WAVE_FORMAT_EXTENSIBLE with no speaker mask: channel n
 *         is output n, as speakers() numbers them. A JUNK chunk keeps room
 *         for RF64's sizes, which a take past 4 GiB becomes at the end (24
 *         channels at 48 kHz, 16 bit, reach it in about half an hour).
 *   FLAC  16 or 24 bit, up to 8 channels (the format's limit): fixed
 *         predictors and Rice codes, a frame of 4096 samples a channel,
 *         each channel coded by itself. Lossless; no MD5 (left as unknown).
 */

// ---- PCM ----------------------------------------------------------------

/** Channels of floats (-1..1) as interleaved little-endian PCM, 16 or 24 bit. */
export function interleavePCM(chans, bits = 16) {
  const n = chans[0]?.length ?? 0, c = chans.length, bps = bits / 8;
  const out = new Uint8Array(n * c * bps);
  const scale = bits === 24 ? 0x7fffff : 0x7fff;
  let o = 0;
  for (let i = 0; i < n; i++) {
    for (let ch = 0; ch < c; ch++) {
      let v = Math.round(Math.max(-1, Math.min(1, chans[ch][i])) * scale);
      if (v < 0) v += bits === 24 ? 0x1000000 : 0x10000;
      out[o++] = v & 0xff;
      out[o++] = (v >> 8) & 0xff;
      if (bits === 24) out[o++] = (v >> 16) & 0xff;
    }
  }
  return out;
}

// ---- WAV ------------------------------------------------------------------

const ascii = (v, o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
const PCM_GUID = [0x01, 0x00, 0x00, 0x00, 0x00, 0x00, 0x10, 0x00, 0x80, 0x00, 0x00, 0xaa, 0x00, 0x38, 0x9b, 0x71];

/** The header of a WAV of these channels, its sizes left 0 (wavSizes() fills them in). */
export function wavHeader(rate, channels, bits = 16) {
  const ext = channels > 2 || bits > 16;
  const fmtLen = ext ? 40 : 16;
  const len = 12 + 36 + 8 + fmtLen + 8;
  const b = new ArrayBuffer(len), v = new DataView(b);
  ascii(v, 0, "RIFF"); ascii(v, 8, "WAVE");
  ascii(v, 12, "JUNK"); v.setUint32(16, 28, true);              // RF64's ds64, if it comes to that
  let o = 48;
  ascii(v, o, "fmt "); v.setUint32(o + 4, fmtLen, true); o += 8;
  const align = channels * bits / 8;
  v.setUint16(o, ext ? 0xfffe : 1, true); v.setUint16(o + 2, channels, true);
  v.setUint32(o + 4, rate, true); v.setUint32(o + 8, rate * align, true);
  v.setUint16(o + 12, align, true); v.setUint16(o + 14, bits, true); o += 16;
  if (ext) {
    v.setUint16(o, 22, true); v.setUint16(o + 2, bits, true);
    v.setUint32(o + 4, 0, true);                                 // no speaker mask: channel n is output n
    PCM_GUID.forEach((x, i) => v.setUint8(o + 8 + i, x)); o += 24;
  }
  ascii(v, o, "data");
  return new Uint8Array(b);
}

/**
 * The sizes at the end, as [{ offset, bytes }] to write over the header
 * (RF64 past 4 GiB), and `pad`: a byte to add after an odd-sized data chunk.
 */
export function wavSizes(rate, channels, bits, dataBytes) {
  const head = wavHeader(rate, channels, bits).length;
  const pad = dataBytes % 2;
  const riff = head - 8 + dataBytes + pad;
  const u32 = x => { const b = new Uint8Array(4); new DataView(b.buffer).setUint32(0, x, true); return b; };
  if (riff <= 0xffffffff) return { pad, patches: [{ offset: 4, bytes: u32(riff) }, { offset: head - 4, bytes: u32(dataBytes) }] };
  const ds = new ArrayBuffer(36), v = new DataView(ds);
  ascii(v, 0, "ds64"); v.setUint32(4, 28, true);
  v.setBigUint64(8, BigInt(riff), true); v.setBigUint64(16, BigInt(dataBytes), true);
  v.setBigUint64(24, BigInt(Math.floor(dataBytes / (channels * bits / 8))), true);
  v.setUint32(32, 0, true);
  const rf = new Uint8Array(8); ascii(new DataView(rf.buffer), 0, "RF64"); new DataView(rf.buffer).setUint32(4, 0xffffffff, true);
  return { pad, patches: [{ offset: 0, bytes: rf }, { offset: 12, bytes: new Uint8Array(ds) }, { offset: head - 4, bytes: u32(0xffffffff) }] };
}

// ---- FLAC -----------------------------------------------------------------

export const FLAC_MAX_CHANNELS = 8;
const BLOCK = 4096;

class BitWriter {
  constructor(size = 1 << 15) { this.buf = new Uint8Array(size); this.pos = 0; this.acc = 0; this.n = 0; }
  byte(b) {
    if (this.pos >= this.buf.length) { const nb = new Uint8Array(this.buf.length * 2); nb.set(this.buf); this.buf = nb; }
    this.buf[this.pos++] = b;
  }
  /** v (0 <= v < 2^n) in n bits, most significant first. */
  bits(v, n) {
    if (n > 24) { this.bits(Math.floor(v / 0x1000000), n - 24); v %= 0x1000000; n = 24; }
    // fewer than 8 bits held: shifted up by at most 24 they stay inside 32
    this.acc = (this.acc << n) | v;
    this.n += n;
    while (this.n >= 8) {
      this.n -= 8;
      this.byte((this.acc >>> this.n) & 0xff);
    }
    this.acc &= (1 << this.n) - 1;
  }
  signed(v, n) { this.bits(v < 0 ? v + 2 ** n : v, n); }
  zeros(q) { while (q >= 24) { this.bits(0, 24); q -= 24; } if (q) this.bits(0, q); }
  align() { if (this.n) this.bits(0, 8 - this.n); }
  bytes() { return this.buf.subarray(0, this.pos); }
}

const CRC8 = new Uint8Array(256), CRC16 = new Uint16Array(256);
for (let i = 0; i < 256; i++) {
  let c = i;
  for (let k = 0; k < 8; k++) c = (c & 0x80 ? (c << 1) ^ 0x07 : c << 1) & 0xff;
  CRC8[i] = c;
  let d = i << 8;
  for (let k = 0; k < 8; k++) d = (d & 0x8000 ? (d << 1) ^ 0x8005 : d << 1) & 0xffff;
  CRC16[i] = d;
}
const crc8 = (b, end) => { let c = 0; for (let i = 0; i < end; i++) c = CRC8[c ^ b[i]]; return c; };
const crc16 = (b, end) => { let c = 0; for (let i = 0; i < end; i++) c = ((c << 8) ^ CRC16[(c >> 8) ^ b[i]]) & 0xffff; return c; };

const RATE_CODES = { 88200: 1, 176400: 2, 192000: 3, 8000: 4, 16000: 5, 22050: 6, 24000: 7, 32000: 8, 44100: 9, 48000: 10, 96000: 11 };

/** fLaC and its STREAMINFO, the sizes and the length left unknown (flacStreamInfo() at the end). */
export function flacHeader(rate, channels, bits) {
  const head = new Uint8Array(8 + 34);
  head.set([0x66, 0x4c, 0x61, 0x43, 0x80, 0, 0, 34]);         // fLaC; the last block, STREAMINFO, 34 bytes
  head.set(flacStreamInfo({ rate, channels, bits, samples: 0, minFrame: 0, maxFrame: 0 }), 8);
  return head;
}

/** STREAMINFO's 34 bytes: written at offset 8 when the take ends. */
export function flacStreamInfo({ rate, channels, bits, samples, minFrame, maxFrame }) {
  const w = new BitWriter(64);
  w.bits(BLOCK, 16); w.bits(BLOCK, 16);
  w.bits(minFrame, 24); w.bits(maxFrame, 24);
  w.bits(rate, 20); w.bits(channels - 1, 3); w.bits(bits - 1, 5);
  w.bits(samples, 36);
  for (let i = 0; i < 16; i++) w.bits(0, 8);                    // MD5: not worked out (unknown)
  return w.bytes().slice();
}

// the residual of fixed predictor `order` at i
const residual = (x, i, order) => {
  switch (order) {
    case 0: return x[i];
    case 1: return x[i] - x[i - 1];
    case 2: return x[i] - 2 * x[i - 1] + x[i - 2];
    case 3: return x[i] - 3 * x[i - 1] + 3 * x[i - 2] - x[i - 3];
    default: return x[i] - 4 * x[i - 1] + 6 * x[i - 2] - 4 * x[i - 3] + x[i - 4];
  }
};
const fold = e => (e >= 0 ? 2 * e : -2 * e - 1);

// a Rice parameter for n folded residuals summing to sum, and about what
// it costs in bits -- from the sum alone, as libFLAC estimates it
function riceParam(sum, n, maxK) {
  if (n <= 0) return { k: 0, cost: 0 };
  const k = sum > n ? Math.min(maxK, Math.floor(Math.log2(sum / n))) : 0;
  return { k, cost: n * (k + 1) + Math.floor(sum / 2 ** k) };
}
const MAX_PARTITION_ORDER = 6;

function writeSubframe(w, x, bits) {
  const n = x.length;
  if (x.every(v => v === x[0])) {                                // constant
    w.bits(0, 8); w.signed(x[0], bits); return;
  }
  const rice2 = bits > 16, maxK = rice2 ? 30 : 14, paramBits = rice2 ? 5 : 4;
  // the predictor whose residuals are smallest ...
  let order = 0, least = Infinity;
  for (let o = 0; o <= Math.min(4, n - 1); o++) {
    let sum = 0;
    for (let i = o; i < n; i++) sum += Math.abs(residual(x, i, o));
    if (sum < least) { least = sum; order = o; }
  }
  const u = new Uint32Array(n);
  for (let i = order; i < n; i++) u[i] = fold(residual(x, i, order));
  // ... and the partitions: sums at the finest, added up for the coarser
  let top = 0;
  while (top < MAX_PARTITION_ORDER && n % (1 << (top + 1)) === 0 && (n >> (top + 1)) > order) top++;
  let sums = new Float64Array(1 << top);
  const fine = n >> top;
  for (let j = 0; j < sums.length; j++) {
    let a = 0;
    for (let i = Math.max(j * fine, order); i < (j + 1) * fine; i++) a += u[i];
    sums[j] = a;
  }
  let best = null;
  for (let p = top; p >= 0; p--) {
    const part = n >> p;
    let cost = order * bits + 6;
    const ks = [];
    for (let j = 0; j < sums.length; j++) {
      const r = riceParam(sums[j], part - (j === 0 ? order : 0), maxK);
      cost += paramBits + r.cost; ks.push(r.k);
    }
    if (!best || cost < best.cost) best = { order, p, ks, u, cost };
    if (p) sums = Float64Array.from({ length: sums.length / 2 }, (_, j) => sums[2 * j] + sums[2 * j + 1]);
  }
  if (best.cost >= n * bits) {                                   // verbatim
    w.bits(0b00000010, 8);
    for (let i = 0; i < n; i++) w.signed(x[i], bits);
    return;
  }
  const { p, ks } = best;
  w.bits(0, 1); w.bits(0b001000 | order, 6); w.bits(0, 1);
  for (let i = 0; i < order; i++) w.signed(x[i], bits);
  w.bits(rice2 ? 1 : 0, 2); w.bits(p, 4);
  const part = n >> p;
  for (let j = 0; j < 1 << p; j++) {
    const k = ks[j], mask = k >= 31 ? 0xffffffff : (2 ** k) - 1;
    w.bits(k, paramBits);
    for (let i = j === 0 ? order : j * part; i < (j + 1) * part; i++) {
      w.zeros(u[i] >>> k);
      w.bits(1, 1);
      if (k) w.bits((u[i] & mask) >>> 0, k);
    }
  }
}

function utf8Number(w, v) {
  if (v < 0x80) { w.bits(v, 8); return; }
  const lens = [[0x800, 2], [0x10000, 3], [0x200000, 4], [0x4000000, 5], [0x80000000, 6]];
  const len = lens.find(([lim]) => v < lim)?.[1] ?? 7;
  const first = len === 7 ? 0xfe : (0xff00 >> len) & 0xff;
  w.bits(first | Math.floor(v / 2 ** (6 * (len - 1))) & (0x7f >> len), 8);
  for (let i = len - 2; i >= 0; i--) w.bits(0x80 | (Math.floor(v / 2 ** (6 * i)) & 0x3f), 8);
}

/**
 * A FLAC encoder, a frame at a time: push(chans) takes channels of floats
 * (-1..1) and gives back the bytes of the frames it could make; end() the
 * rest. info() is what flacStreamInfo() wants for the header at the end.
 */
export function flacEncoder({ rate, channels, bits = 16 }) {
  if (channels > FLAC_MAX_CHANNELS) throw new Error(`FLAC takes at most ${FLAC_MAX_CHANNELS} channels`);
  const scale = bits === 24 ? 0x7fffff : 0x7fff;
  const held = Array.from({ length: channels }, () => new Int32Array(BLOCK));
  let fill = 0, frame = 0, samples = 0, minFrame = 0, maxFrame = 0;

  const encode = n => {
    const w = new BitWriter(n * channels * (bits / 8) + 64);
    w.bits(0xfff8, 16);                                          // sync, fixed block size
    w.bits(n === BLOCK ? 12 : 7, 4);
    w.bits(RATE_CODES[rate] ?? 0, 4);
    w.bits(channels - 1, 4);                                     // each channel by itself
    w.bits(bits === 24 ? 6 : bits === 16 ? 4 : 0, 3);
    w.bits(0, 1);
    utf8Number(w, frame++);
    if (n !== BLOCK) w.bits(n - 1, 16);
    w.bits(crc8(w.buf, w.pos), 8);
    for (let ch = 0; ch < channels; ch++) writeSubframe(w, held[ch].subarray(0, n), bits);
    w.align();
    w.bits(crc16(w.buf, w.pos), 16);
    const out = w.bytes().slice();
    samples += n;
    minFrame = minFrame ? Math.min(minFrame, out.length) : out.length;
    maxFrame = Math.max(maxFrame, out.length);
    return out;
  };

  return {
    push(chans) {
      const out = [];
      const len = chans[0]?.length ?? 0;
      for (let i = 0; i < len; i++) {
        for (let ch = 0; ch < channels; ch++) {
          const v = chans[ch]?.[i] ?? 0;
          held[ch][fill] = Math.round(Math.max(-1, Math.min(1, v)) * scale);
        }
        if (++fill === BLOCK) { out.push(encode(BLOCK)); fill = 0; }
      }
      return out;
    },
    end() { const out = fill ? [encode(fill)] : []; fill = 0; return out; },
    info: () => ({ rate, channels, bits, samples, minFrame, maxFrame }),
  };
}
